// src/components/ui/primitives/dropzone.tsx

/**
 * @fileoverview Dropzone component with multiple variants including UploadThing integration
 * @description A flexible dropzone component that supports both standard file uploads and UploadThing integration
 * @module ui/primitives/dropzone
 */

"use client"

// Upload failures are logged by error TYPE only (never the raw Error): UploadThing's
// file-type errors embed the raw File.name (potential PII — resumes, statements),
// and logClientError would forward the message + stack to the drain, bypassing the
// redactUploadFileName policy. The user still sees the full message in the toast.
import { clientLogger } from '~/lib/logger-client'
import { UploadButton, UploadDropzone } from "@uploadthing/react"
import { UploadIcon, Upload, FileImage } from "lucide-react"
import type { ReactNode } from "react"
import { createContext, useContext, useMemo } from "react"
import type { DropEvent, DropzoneOptions, FileRejection } from "react-dropzone"
import { useDropzone } from "react-dropzone"
import { toast } from "sonner"
import { Button } from "~/components/ui/primitives/button"
import RiLoader2Fill from "~/icons/remix/generated/RiLoader2Fill"
import type { OurFileRouter } from "~/lib/uploadthing"
import { cn } from "~/lib/utils"

/**
 * Type for the upload response from our file router
 * This matches the return type from our onUploadComplete handlers
 */
export interface UploadedFileData {
  uploadedBy: string
  url: string
  name: string
  size: number
  type: string
}

/**
 * Type for the client-side upload response
 * This is what UploadThing provides to the client
 */
export interface ClientUploadResponse {
  url: string
  name: string
  size: number
  type: string
}

/**
 * Context type for the Dropzone component
 * @interface DropzoneContextType
 * 
 * @property {File[]} src - Array of files currently in the dropzone
 * @property {DropzoneOptions["accept"]} accept - Accepted file types
 * @property {DropzoneOptions["maxSize"]} maxSize - Maximum file size in bytes
 * @property {DropzoneOptions["minSize"]} minSize - Minimum file size in bytes
 * @property {DropzoneOptions["maxFiles"]} maxFiles - Maximum number of files allowed
 */
type DropzoneContextType = {
  /** Array of files currently in the dropzone */
  src?: File[]
  /** Accepted file types */
  accept?: DropzoneOptions["accept"]
  /** Maximum file size in bytes */
  maxSize?: DropzoneOptions["maxSize"]
  /** Minimum file size in bytes */
  minSize?: DropzoneOptions["minSize"]
  /** Maximum number of files allowed */
  maxFiles?: DropzoneOptions["maxFiles"]
}

/**
 * Renders bytes in a human-readable format
 * @param {number} bytes - The number of bytes to format
 * @returns {string} The formatted byte string
 */
const renderBytes = (bytes: number): string => {
  const units = ["B", "KB", "MB", "GB", "TB", "PB"] as const
  let size = bytes
  let unitIndex = 0

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }

  return `${size.toFixed(2)}${units[unitIndex]}`
}

const DropzoneContext = createContext<DropzoneContextType | undefined>(
  undefined
)

/**
 * Props for the Dropzone component
 * @interface DropzoneProps
 * 
 * @property {File[]} src - Array of files currently in the dropzone
 * @property {string} className - Additional CSS class names
 * @property {DropzoneProps["onDrop"]} onDrop - Callback function called when files are dropped
 * @property {ReactNode} children - Child components to render inside the dropzone
 */
export type DropzoneProps = Omit<DropzoneOptions, "onDrop"> & {
  /** Array of files currently in the dropzone */
  src?: File[]
  /** Additional CSS class names */
  className?: string
  /** Callback function called when files are dropped */
  onDrop?: (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
    event: DropEvent
  ) => void
  /** Child components to render inside the dropzone */
  children?: ReactNode
}

/**
 * Dropzone component that allows users to upload files
 * @param {DropzoneProps} props - The component props
 * @returns {JSX.Element} The rendered dropzone component
 */
export const Dropzone = ({
  accept,
  maxFiles = 1,
  maxSize,
  minSize,
  onDrop,
  onError,
  disabled,
  src,
  className,
  children,
  ...props
}: DropzoneProps): JSX.Element => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    maxFiles,
    maxSize,
    minSize,
    onError,
    disabled,
    onDrop: (acceptedFiles, fileRejections, event) => {
      if (fileRejections.length > 0) {
        const message = fileRejections.at(0)?.errors.at(0)?.message
        onError?.(new Error(message))
        return
      }

      onDrop?.(acceptedFiles, fileRejections, event)
    },
    ...props,
  })

  const contextValue = useMemo(
    () => ({ src, accept, maxSize, minSize, maxFiles }),
    [src, accept, maxSize, minSize, maxFiles]
  )

  return (
    <DropzoneContext.Provider
      key={JSON.stringify(src)}
      value={contextValue}
    >
      <Button
        className={cn(
          "relative h-auto w-full flex-col overflow-hidden p-8",
          isDragActive && "outline-none ring-1 ring-ring",
          className
        )}
        disabled={disabled}
        type="button"
        variant="outline"
        {...getRootProps()}
      >
        <input {...getInputProps()} disabled={disabled} />
        {children}
      </Button>
    </DropzoneContext.Provider>
  )
}

/**
 * Hook to access the dropzone context
 * @returns {DropzoneContextType} The dropzone context
 * @throws {Error} When used outside of a Dropzone component
 */
const useDropzoneContext = (): DropzoneContextType => {
  const context = useContext(DropzoneContext)

  if (!context) {
    throw new Error("useDropzoneContext must be used within a Dropzone")
  }

  return context
}

/**
 * Props for the DropzoneContent component
 * @interface DropzoneContentProps
 * 
 * @property {ReactNode} children - Child components to render
 * @property {string} className - Additional CSS class names
 */
export type DropzoneContentProps = {
  /** Child components to render */
  children?: ReactNode
  /** Additional CSS class names */
  className?: string
}

/** Maximum number of file names to display in the label */
const maxLabelItems = 3

/**
 * Component that displays content when files are present in the dropzone
 * @param {DropzoneContentProps} props - The component props
 * @returns {JSX.Element | null} The rendered content or null if no files
 */
export const DropzoneContent = ({
  children,
  className,
}: DropzoneContentProps): JSX.Element | null => {
  const { src } = useDropzoneContext()

  if (!src) {
    return null
  }

  if (children) {
    return <>{children}</>
  }

  return (
    <div className={cn("flex flex-col items-center justify-center", className)}>
      <div className="flex size-8 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <UploadIcon size={16} />
      </div>
      <p className="my-2 w-full truncate font-medium text-sm">
        {src.length > maxLabelItems
          ? `${new Intl.ListFormat("en").format(
              src.slice(0, maxLabelItems).map((file) => file.name)
            )} and ${src.length - maxLabelItems} more`
          : new Intl.ListFormat("en").format(src.map((file) => file.name))}
      </p>
      <p className="w-full text-wrap text-muted-foreground text-xs">
        Drag and drop or click to replace
      </p>
    </div>
  )
}

/**
 * Props for the DropzoneEmptyState component
 * @interface DropzoneEmptyStateProps
 * 
 * @property {ReactNode} children - Child components to render
 * @property {string} className - Additional CSS class names
 */
export type DropzoneEmptyStateProps = {
  /** Child components to render */
  children?: ReactNode
  /** Additional CSS class names */
  className?: string
}

/**
 * Component that displays empty state when no files are present in the dropzone
 * @param {DropzoneEmptyStateProps} props - The component props
 * @returns {JSX.Element | null} The rendered empty state or null if files are present
 */
export const DropzoneEmptyState = ({
  children,
  className,
}: DropzoneEmptyStateProps): JSX.Element | null => {
  const { src, accept, maxSize, minSize, maxFiles } = useDropzoneContext()

  if (src) {
    return null
  }

  if (children) {
    return <>{children}</>
  }

  let caption = ""

  if (accept) {
    caption += "Accepts "
    caption += new Intl.ListFormat("en").format(Object.keys(accept))
  }

  if (minSize && maxSize) {
    caption += ` between ${renderBytes(minSize)} and ${renderBytes(maxSize)}`
  } else if (minSize) {
    caption += ` at least ${renderBytes(minSize)}`
  } else if (maxSize) {
    caption += ` less than ${renderBytes(maxSize)}`
  }

  return (
    <div className={cn("flex flex-col items-center justify-center", className)}>
      <div className="flex size-8 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <UploadIcon size={16} />
      </div>
      <p className="my-2 w-full truncate text-wrap font-medium text-sm">
        Upload {maxFiles === 1 ? "a file" : "files"}
      </p>
      <p className="w-full truncate text-wrap text-muted-foreground text-xs">
        Drag and drop or click to upload
      </p>
      {caption && (
        <p className="text-wrap text-muted-foreground text-xs">{caption}.</p>
      )}
    </div>
  )
}

/**
 * Custom upload button component with modern styling
 */
export function CustomUploadButton(): JSX.Element {
  return (
    <UploadButton<OurFileRouter, "imageUploader">
      endpoint="imageUploader"
      onClientUploadComplete={(res: ClientUploadResponse[]) => {
        toast.success("Upload successful", {
          description: `Successfully uploaded ${res.length} file(s)`,
        })
      }}
      onUploadError={(error: Error) => {
        clientLogger.error("Upload failed", { component: "Dropzone", errorName: error.name })
        toast.error("Upload failed", {
          description: error.message,
        })
      }}
      onUploadBegin={(name: string) => {
        toast.loading("Uploading", {
          description: `Uploading ${name}...`,
        })
      }}
      className="ut-button:bg-primary ut-button:hover:bg-primary/90 ut-button:text-primary-foreground ut-button:border-0 ut-button:rounded-lg ut-button:px-6 ut-button:py-3 ut-button:font-medium ut-button:transition-all ut-button:duration-200 ut-button:shadow-sm ut-button:hover:shadow-md ut-button:focus:ring-2 ut-button:focus:ring-primary/20 ut-button:focus:outline-none"
    />
  )
}

/**
 * Custom upload dropzone component with modern styling
 */
export function CustomUploadDropzone(): JSX.Element {
  return (
    <UploadDropzone<OurFileRouter, "imageUploader">
      endpoint="imageUploader"
      onClientUploadComplete={(res: ClientUploadResponse[]) => {
        toast.success("Upload successful", {
          description: `Successfully uploaded ${res.length} file(s)`,
        })
      }}
      onUploadError={(error: Error) => {
        clientLogger.error("Upload failed", { component: "Dropzone", errorName: error.name })
        toast.error("Upload failed", {
          description: error.message,
        })
      }}
      onUploadBegin={(name: string) => {
        toast.loading("Uploading", {
          description: `Uploading ${name}...`,
        })
      }}
      className="ut-dropzone:border-2 ut-dropzone:border-dashed ut-dropzone:border-border ut-dropzone:bg-background ut-dropzone:hover:bg-muted/50 ut-dropzone:rounded-xl ut-dropzone:p-8 ut-dropzone:transition-all ut-dropzone:duration-200 ut-label:text-foreground ut-label:font-medium ut-allowed-content:text-muted-foreground ut-allowed-content:text-sm ut-button:bg-primary ut-button:hover:bg-primary/90 ut-button:text-primary-foreground ut-button:rounded-lg ut-button:px-4 ut-button:py-2 ut-button:font-medium ut-button:transition-all ut-button:duration-200 ut-button:shadow-sm ut-button:hover:shadow-md"
    />
  )
}

/**
 * Enhanced uploader component for generator tools with professional styling
 * 
 * @param {Object} props - Component props
 *   @param {Function} [props.onUploadCompleteAction] - Callback when upload completes successfully
 *   @param {Function} [props.onUploadErrorAction] - Callback when upload fails
 *   @param {string} [props.className=""] - Additional CSS classes
 *   @param {string} [props.maxFileSize="4MB"] - Maximum file size display text
 * @returns {JSX.Element} The enhanced uploader component
 */
export function GeneratorImageUploader({
  onUploadCompleteAction,
  onUploadErrorAction,
  className = "",
  maxFileSize = "4MB",
}: {
  onUploadCompleteAction?: (res: ClientUploadResponse[]) => void
  onUploadErrorAction?: (error: Error) => void
  className?: string
  maxFileSize?: string
}): JSX.Element {
  return (
    <div className={`relative ${className}`}>
      <UploadDropzone<OurFileRouter, "imageUploader">
        endpoint="imageUploader"
        onClientUploadComplete={(res: ClientUploadResponse[]) => {
          toast.success("Upload successful", {
            description: `Successfully uploaded ${res.length} file(s)`,
          })
          onUploadCompleteAction?.(res)
        }}
        onUploadError={(error: Error) => {
          clientLogger.error("Upload failed", { component: "Dropzone", errorName: error.name })
          toast.error("Upload failed", {
            description: error.message,
          })
          onUploadErrorAction?.(error)
        }}
        onUploadBegin={(name: string) => {
          toast.loading("Uploading", {
            description: `Uploading ${name}...`,
          })
        }}
        config={{
          mode: "auto",
        }}
        content={{
          uploadIcon: () => (
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="rounded-full bg-primary/10 p-4">
                <FileImage className="h-8 w-8 text-primary" />
              </div>
            </div>
          ),
          label: () => (
            <div className="text-center">
              <p className="text-lg font-semibold text-foreground mb-1">
                Drop your image here
              </p>
              <p className="text-sm text-muted-foreground">
                or click to browse files
              </p>
            </div>
          ),
          allowedContent: () => (
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground mt-4">
              <span>Supports: JPG, PNG, GIF, WebP</span>
              <span>•</span>
              <span>Max size: {maxFileSize}</span>
            </div>
          ),
          button: ({ ready, isUploading }) => (
            <div className="mt-6 flex justify-center mb-6">
              {ready && !isUploading && (
                <div className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-all duration-200 hover:bg-primary/90 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/20">
                  <Upload className="h-4 w-4 flex-shrink-0" />
                  <span className="whitespace-nowrap">Choose File</span>
                </div>
              )}
              {isUploading && (
                <div className="inline-flex items-center gap-2 rounded-lg bg-muted px-4 py-2 text-sm font-medium text-muted-foreground">
                  <RiLoader2Fill className="h-4 w-4 flex-shrink-0 animate-spin" />
                  <span className="whitespace-nowrap">Uploading...</span>
                </div>
              )}
            </div>
          ),
        }}
        className="ut-dropzone:border-2 ut-dropzone:border-dashed ut-dropzone:border-border ut-dropzone:bg-background ut-dropzone:hover:bg-muted/30 ut-dropzone:rounded-xl ut-dropzone:p-6 ut-dropzone:transition-all ut-dropzone:duration-300 ut-dropzone:cursor-pointer ut-dropzone:group ut-dropzone:focus:outline-none ut-dropzone:focus:ring-2 ut-dropzone:focus:ring-primary/20 ut-dropzone:focus:border-primary ut-dropzone:min-h-[280px] ut-dropzone:flex ut-dropzone:flex-col ut-dropzone:items-center ut-dropzone:justify-center ut-label:text-foreground ut-label:font-medium ut-allowed-content:text-muted-foreground ut-allowed-content:text-sm ut-button:bg-transparent ut-button:hover:bg-transparent ut-button:text-transparent ut-button:rounded-lg ut-button:px-0 ut-button:py-0 ut-button:font-medium ut-button:transition-all ut-button:duration-200 ut-button:shadow-none ut-button:hover:shadow-none ut-button:focus:ring-0 ut-button:focus:outline-none ut-button:border-0"
      />
    </div>
  )
}

/**
 * Compact upload button for inline use
 * 
 * @param {Object} props - Component props
 *   @param {Function} [props.onUploadCompleteAction] - Callback when upload completes successfully
 *   @param {Function} [props.onUploadErrorAction] - Callback when upload fails
 *   @param {string} [props.className=""] - Additional CSS classes
 *   @param {'default' | 'outline' | 'ghost'} [props.variant='default'] - Button style variant
 * @returns {JSX.Element} The compact upload button component
 */
export function CompactUploadButton({
  onUploadCompleteAction,
  onUploadErrorAction,
  className = "",
  variant = "default",
}: {
  onUploadCompleteAction?: (res: ClientUploadResponse[]) => void
  onUploadErrorAction?: (error: Error) => void
  className?: string
  variant?: "default" | "outline" | "ghost"
}): JSX.Element {
  const variantStyles = {
    default: "ut-button:bg-primary ut-button:hover:bg-primary/90 ut-button:text-primary-foreground",
    outline: "ut-button:border ut-button:border-border ut-button:bg-background ut-button:hover:bg-muted ut-button:text-foreground",
    ghost: "ut-button:bg-transparent ut-button:hover:bg-muted ut-button:text-foreground",
  }

  return (
    <UploadButton<OurFileRouter, "imageUploader">
      endpoint="imageUploader"
      onClientUploadComplete={(res: ClientUploadResponse[]) => {
        toast.success("Upload successful", {
          description: `Successfully uploaded ${res.length} file(s)`,
        })
        onUploadCompleteAction?.(res)
      }}
      onUploadError={(error: Error) => {
        clientLogger.error("Upload failed", { component: "Dropzone", errorName: error.name })
        toast.error("Upload failed", {
          description: error.message,
        })
        onUploadErrorAction?.(error)
      }}
      onUploadBegin={(name: string) => {
        toast.loading("Uploading", {
          description: `Uploading ${name}...`,
        })
      }}
      content={{
        button: ({ isUploading }) => (
          <div className="flex items-center gap-2">
            {isUploading ? (
              <RiLoader2Fill className="h-4 w-4 animate-spin flex-shrink-0" />
            ) : (
              <Upload className="h-4 w-4 flex-shrink-0" />
            )}
            <span className="whitespace-nowrap">{isUploading ? "Uploading..." : "Upload Image"}</span>
          </div>
        ),
      }}
      className={`${variantStyles[variant]} ut-button:border-0 ut-button:rounded-lg ut-button:px-4 ut-button:py-2 ut-button:text-sm ut-button:font-medium ut-button:transition-all ut-button:duration-200 ut-button:shadow-sm ut-button:hover:shadow-md ut-button:focus:ring-2 ut-button:focus:ring-primary/20 ut-button:focus:outline-none ut-button:disabled:opacity-50 ut-button:disabled:cursor-not-allowed ${className}`}
    />
  )
}
